"use client";

import React from "react";
import { Ticket, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { useRouter } from "next/navigation";
import { useModalControls } from "@/lib/modals";

// Types
import type { TabContentProps } from "@/components/shared/UniversalDetailPage/types/detailPage";
import type { Time_Entries } from "@/src/gql/graphql";

/**
 * Ticket Details Tab Component
 *
 * Displays ticket information associated with a time entry.
 * Shows ticket number, title, status, priority, and description.
 * 
 * UPDATED: Now uses the type-safe modal URL system from @/lib/modals
 */
export function TicketDetailsTab({ item }: TabContentProps<Time_Entries>) {
  const router = useRouter();
  const { openModal: openTicketModal } = useModalControls("tickets-detail");
  const ticket = (item as any).tickets;

  if (!ticket) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-sm text-gray-500">No ticket associated with this time entry</p>
        </CardContent>
      </Card>
    );
  }

  const handleViewFullTicket = () => {
    openTicketModal({ ticketsId: ticket.id });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Ticket className="w-5 h-5 text-gray-600" />
          <h3 className="text-lg font-semibold text-card-foreground">Ticket Details</h3>
        </div>
        <Button variant="outline" size="sm" onClick={handleViewFullTicket} className="gap-2">
          <ExternalLink className="w-4 h-4" />
          View Full Ticket
        </Button>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-500">Ticket Number</label>
              <p className="text-sm text-card-foreground mt-1">{ticket.ticket_number || "N/A"}</p>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-500">Title</label>
              <p className="text-sm text-card-foreground mt-1">{ticket.title || "N/A"}</p>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-500">Status</label>
              <div className="mt-1">
                <Badge variant="outline">{ticket.status || "Unknown"}</Badge>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-500">Priority</label>
              <div className="mt-1">
                <Badge
                  variant={
                    ticket.priority === "high"
                      ? "destructive"
                      : ticket.priority === "medium"
                        ? "default"
                        : "secondary"
                  }
                >
                  {ticket.priority || "Unknown"}
                </Badge>
              </div>
            </div>

            {ticket.description && (
              <div>
                <label className="text-sm font-medium text-gray-500">Description</label>
                <p className="text-sm text-card-foreground mt-1 whitespace-pre-wrap">
                  {ticket.description}
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
